import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useColors } from '../theme/ColorModeContext';

export interface PathSegment {
    label: string;
    to?: string;
}

interface CyberpunkTitleProps {
    title?: string;
    segments?: PathSegment[];
    subtitle?: string;
    badge?: string;
    mb?: number | string;
}

const CyberpunkTitle = ({ title, segments, subtitle, badge, mb = '32px' }: CyberpunkTitleProps) => {
    const colors = useColors();

    const titleSx = {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 800,
        fontSize: { xs: '1.8rem', md: '2.65rem' },
        letterSpacing: 0,
        textTransform: 'uppercase' as const,
        color: colors.text.primary,
        lineHeight: 1.12,
    };

    return (
        <Box sx={{ marginBottom: mb }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {/* // prefix */}
                <Typography component="span" sx={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: { xs: '1.1rem', md: '1.4rem' },
                    color: colors.accent.cyan,
                    opacity: 0.9,
                    letterSpacing: 0,
                    lineHeight: 1,
                    userSelect: 'none',
                }}>
                    {'//'}
                </Typography>

                {segments ? (
                    /* Path-style title: Category / ChallengeName */
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        {segments.map((seg, i) => {
                            const isLast = i === segments.length - 1;
                            const text = (
                                <Typography component="span" sx={{
                                    ...titleSx,
                                    color: isLast ? colors.text.primary : colors.text.secondary,
                                    opacity: isLast ? 1 : 0.7,
                                    ...(seg.to && !isLast && {
                                        transition: 'color 0.2s ease, opacity 0.2s ease',
                                        '&:hover': { color: colors.accent.cyan, opacity: 1 },
                                    }),
                                }}>
                                    {seg.label}
                                </Typography>
                            );
                            return (
                                <React.Fragment key={i}>
                                    {seg.to && !isLast
                                        ? <Link to={seg.to} style={{ textDecoration: 'none' }}>{text}</Link>
                                        : text
                                    }
                                    {!isLast && (
                                        <Typography component="span" sx={{
                                            ...titleSx,
                                            color: colors.text.muted,
                                            opacity: 0.5,
                                            userSelect: 'none',
                                        }}>
                                            /
                                        </Typography>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                ) : (
                    // Simple single title
                    <Typography variant="h3" component="span" sx={titleSx}>
                        {title}
                    </Typography>
                )}

                {badge && (
                    <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: 'rgba(0, 230, 118, 0.08)',
                        border: `1px solid rgba(0, 230, 118, 0.35)`,
                        borderRadius: '6px',
                        padding: '3px 10px',
                    }}>
                        <Box sx={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: colors.accent.green,
                            animation: 'subtlePulse 2s ease-in-out infinite',
                            flexShrink: 0,
                        }} />
                        <Typography component="span" sx={{
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: '0.68rem',
                            color: colors.accent.green,
                            fontWeight: 700,
                            letterSpacing: 0,
                        }}>
                            {badge}
                        </Typography>
                    </Box>
                )}
            </Box>
            {subtitle && (
                <Typography component="span" sx={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: '0.72rem',
                    color: colors.text.secondary,
                    letterSpacing: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 0,
                }}>
                    <Box component="span" sx={{ color: colors.accent.cyan, fontSize: '0.8rem' }}>◈</Box>
                    {subtitle}
                </Typography>
            )}
            </Box>

            {/* Landing-style section rule */}
            <Box sx={{
                height: '1px',
                mt: '14px',
                background: `linear-gradient(90deg, ${colors.accent.cyan} 0%, ${colors.accent.cyanGlow} 36%, transparent 100%)`,
                borderRadius: '1px',
                transformOrigin: 'left',
                animation: 'expandLine 0.5s ease-out',
            }} />

        </Box>
    );
};

export default CyberpunkTitle;
